import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { ScreenBackdrop } from './ScreenBackdrop';
import { AddressSheet } from './AddressSheet';
import { NetworkSelect } from './NetworkSelect';
import { ReauthFlow } from './ReauthFlow';
import { ChevronRight, CopyIcon, DiagonalArrow } from './icons';
import { DEFAULT_COIN, COINS, type Coin } from './TokenSheet';
import { useWallet } from '../../hooks/useWallet';
import { useMe } from '../../hooks/useMe';
import { useTelegram } from '../../hooks/useTelegram';
import { getSeed } from '../../lib/wallet';
import { ensureWalletAddresses } from '../../lib/addressBackfill';
import { addressDisplayName } from '../../lib/walletNames';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

export function ReceiveScreen({ onClose, coin = DEFAULT_COIN }: { onClose: () => void; coin?: Coin }) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const { userId } = useTelegram();
  const [copied, setCopied] = useState<'address' | 'telegramId' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [selectedAddr, setSelectedAddr] = useState<string | null>(null);

  // Network switcher: user can flip between chains for the same symbol.
  const [activeChain, setActiveChain] = useState(coin.chain);
  const activeCoin = COINS.find((c) => c.symbol === coin.symbol && c.chain === activeChain) ?? coin;
  const altCoins = COINS.filter((c) => c.symbol === coin.symbol);

  const { wallet } = useWallet();
  const walletName = wallet?.name ?? 'Main wallet';

  // Real deposit addresses for this coin's chain (EOA — funds arrive here).
  // Default to the first; the top selector switches between them.
  const chainAddresses = (wallet?.addresses ?? []).filter((a) => a.chain === activeCoin.chain);
  const activeIdx = Math.max(
    0,
    chainAddresses.findIndex((a) => a.eoaAddress === selectedAddr),
  );
  const active = chainAddresses[activeIdx];
  const address = active?.eoaAddress ?? '';
  // Same shared naming as the coin detail / address selector.
  const activeName = active ? addressDisplayName(active, t('nav.wallet')) : walletName;

  const copy = (value: string, type: 'address' | 'telegramId') => {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(type);
        setToast(t('coin.copied'));
        window.setTimeout(() => {
          setCopied(null);
          setToast(null);
        }, 1500);
      },
      () => {},
    );
  };

  const share = () => {
    if (!address) return;
    const wa = window.Telegram?.WebApp;
    if (wa?.openTelegramLink) {
      wa.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(address)}`);
    } else if (navigator.share) {
      void navigator.share({ text: address }).catch(() => {});
    }
  };

  // Last-resort BTC activation: a PIN user whose wallet predates Bitcoin support
  // and who reaches Receive→Bitcoin before ANY reauth ever happened. Everyone
  // else gets the address silently (boot / reauth / send backfill hooks).
  if (activeCoin.chain === 'btc' && chainAddresses.length === 0) {
    return <ActivateBtcScreen onClose={onClose} coin={activeCoin} />;
  }

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {/* Header: close + wallet selector */}
        <div className="relative flex h-10 items-center justify-center">
          {!tgBack && (
            <button
              onClick={onClose}
              className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-white" />
            </button>
          )}
          <button
            onClick={() => setWalletOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-white py-1.5 pl-1.5 pr-3 shadow-card"
          >
            <activeCoin.Icon size={24} />
            <span className="text-[14px] font-medium text-ink">{activeName}</span>
            <ChevronRight size={16} className="rotate-90 text-muted" />
          </button>
        </div>

        {/* QR */}
        <div className="mt-8 flex justify-center">
          <div className="rounded-[28px] bg-white p-6 shadow-card">
            <QRCodeSVG
              value={address || ' '}
              size={208}
              bgColor="#ffffff"
              fgColor="#121212"
              level="M"
              marginSize={0}
            />
          </div>
        </div>

        <NetworkSelect
          coins={altCoins}
          value={activeChain}
          onChange={(chain) => {
            setActiveChain(chain);
            setSelectedAddr(null);
          }}
          className="mt-6 flex items-center gap-1 self-center rounded-full bg-black/[0.06] py-1.5 pl-3 pr-2 active:opacity-70"
        />

        {/* Address */}
        <div className="mt-6 flex items-center justify-between">
          <span className="text-[13px] text-muted">{t('receive.myAddress')}</span>
          <span className="text-[13px] text-muted">
            {activeCoin.symbol} ({activeCoin.network})
          </span>
        </div>
        <button
          onClick={() => copy(address, 'address')}
          className="mt-2 flex items-center gap-3 rounded-[20px] bg-white p-4 text-left shadow-card"
        >
          <span className="min-w-0 flex-1 break-all text-[15px] text-ink">
            {address || t('common.loading')}
          </span>
          <CopyIcon size={22} className={copied === 'address' ? 'text-positive' : 'text-[#377dff]'} />
        </button>

        {/* Telegram ID — an IncptWallet user can send to this ID on any
            supported chain; the backend resolves it to the primary address. */}
        {userId && (
          <>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-[13px] text-muted">{t('receive.telegramId')}</span>
              <span className="text-[12px] text-muted">{t('receive.telegramIdHint')}</span>
            </div>
            <button
              onClick={() => copy(String(userId), 'telegramId')}
              className="mt-2 flex items-center gap-3 rounded-[20px] bg-white p-4 text-left shadow-card"
            >
              <span className="min-w-0 flex-1 text-[15px] text-ink">{userId}</span>
              <CopyIcon
                size={22}
                className={copied === 'telegramId' ? 'text-positive' : 'text-[#377dff]'}
              />
            </button>
          </>
        )}

        {/* Warning */}
        <p className="mt-5 px-4 text-center text-[13px] leading-[1.5] text-muted">
          {t('receive.warn1')} <span className="font-medium text-ink">{activeCoin.symbol}</span>{' '}
          {t('receive.warn2')} <span className="font-medium text-ink">{activeCoin.network}</span>{' '}
          {t('receive.warn3')}
        </p>

        <div className="flex-1" />

        {/* Share */}
        <button
          onClick={share}
          disabled={!address}
          className="flex h-14 items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-50"
        >
          {t('receive.share')}
          <DiagonalArrow size={20} className="text-white" />
        </button>
      </div>

      {/* Wallet selector */}
      <AddressSheet
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        coin={activeCoin}
        title={t('send.selectWallet')}
        onSelect={(addr) => {
          setSelectedAddr(addr);
          setWalletOpen(false);
        }}
      />

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[14px] font-medium text-white shadow-[0px_10px_30px_rgba(0,0,0,0.25)]">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

/** One-tap Bitcoin activation for pre-BTC wallets whose seed is PIN-locked:
 *  reauth → getSeed → derive + register the bc1q address → the normal Receive
 *  screen takes over (wallet query invalidation re-renders the parent). */
function ActivateBtcScreen({ onClose, coin }: { onClose: () => void; coin: Coin }) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const { me, initData, canFetch } = useMe();
  const { wallet } = useWallet();
  const qc = useQueryClient();
  const [reauthOpen, setReauthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;

  const activate = async (token?: string) => {
    if (!canFetch) return;
    setBusy(true);
    setFailed(false);
    try {
      const m = await getSeed(initData, token);
      if (!m) throw new Error('seed_unavailable');
      await ensureWalletAddresses(initData, m, wallet, () =>
        qc.invalidateQueries({ queryKey: ['wallet'] }),
      );
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {!tgBack && (
          <div className="flex h-10 items-center">
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-white" />
            </button>
          </div>
        )}
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <coin.Icon size={64} />
          <h1 className="text-[20px] font-semibold text-ink">{t('receive.activateBtc')}</h1>
          <p className="text-[14px] leading-[1.5] text-muted">{t('receive.activateBtcHint')}</p>
          {failed && (
            <p className="text-[13px] font-medium text-[#ff3b30]">{t('send.errGeneric')}</p>
          )}
          <button
            onClick={() => (needsReauth ? setReauthOpen(true) : void activate())}
            disabled={busy}
            className={`mt-2 flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white ${
              busy ? 'pointer-events-none opacity-60' : 'active:opacity-90'
            }`}
          >
            {busy ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              t('receive.activateBtcCta')
            )}
          </button>
        </div>
      </div>
      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              void activate(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
